/**
 * OutlineEditTool — the leather "Edit points" method (epic #781):
 * direct point editing of leather outlines. #969 shipped piece
 * outlines; #970 adds cutouts (closed, curves allowed) and stitch
 * lines (open or closed point lists — no bezier curves, holes
 * re-derive from the committed points).
 *
 * Composes the lean `OutlineEditController` (anchor / bezier-handle /
 * curve drags) against a mode-specific `OutlineTarget`, plus the tap
 * actions: add-point, the four-state bezier cycle (curve-capable
 * targets only — same cycle + per-anchor locks as construction), and
 * delete-point (refused below the target's minimum — a closed outline
 * keeps 3 points, an open seam keeps 2). Piece and cutout outlines
 * persist their locks / handle snapshots in `smoothAnchors` /
 * `bezierMemory` model fields, remapped across insertions and
 * deletions with the shared helpers construction uses.
 */
import paper from "paper";
import { ToolBase } from "./ToolBase";
import type { PointerSample } from "../canvas/PointerInput";
import type { EditorContext, ToolSettings } from "./EditorContext";
import type { OutlineEditHandlesSpec, Scene } from "../canvas/Scene";
import {
  pieceStitchLines,
  type Cutout,
  type Piece,
  type StitchLine,
  type Vec2,
} from "../model/Project";
import { updatePiece } from "../model/projectOps";
import { markFreehandEdited } from "../model/leatherSmoothing";
import { seamFromPathData, seamPathData } from "../model/seamGeometry";
import { showCanvasToast } from "../canvasToast";
import { pickLeatherObjectAt } from "./leatherPick";
import { setCanvasCursor } from "../canvas/canvasCursor";
import {
  OutlineEditController,
  type OutlineEditHost,
  type OutlineTarget,
} from "./outlineEditController";
import {
  allSubpathsClosed,
  deleteOutlineAnchor,
  insertAnchorOnPathData,
  remapAnchorMetaAfterDelete,
  remapAnchorMetaAfterInsert,
  resolveSubpaths,
  type OutlineAnchorRef,
} from "./outlineEditGeometry";
import { cycleAnchorBezier } from "../model/constructionAnchors";
import { clearPieceRounding } from "../model/pieceRounding";
import { pieceDisplayOffset, translatePathData } from "../model/pieceTransform";

export type OutlineEditAction =
  | "drag"
  | "smooth-toggle"
  | "add-point"
  | "delete";

const LINKED_PIECE_HINT =
  "Outline follows construction lines — detach the part to edit points";
const LINKED_CUTOUT_HINT =
  "Cutout follows construction lines — detach it to edit points";
const LINKED_STITCH_HINT =
  "Seam follows construction lines — detach it to edit points";
const CROPPED_STITCH_HINT =
  "Seam overhangs the part — move it fully onto the leather to edit points";

/** The lock/memory slice piece and cutout outlines share. */
type OutlineMeta = {
  smoothAnchors: Piece["smoothAnchors"];
  bezierMemory: Piece["bezierMemory"];
};

export class OutlineEditTool extends ToolBase implements OutlineEditHost {
  id = "outlineEdit";
  label = "Edit points";
  cursor = "default";

  private editAction: OutlineEditAction = "drag";
  private controller = new OutlineEditController(this);
  /** Pending smooth-toggle tap (fires on release within the slop). */
  private smoothTogglePending: { x: number; y: number } | null = null;
  /** UI sync hook — app.ts wires this to highlight the action row. */
  onEditActionChanged: (() => void) | null = null;

  constructor(ctx: EditorContext) {
    super(ctx);
  }

  setEditAction(action: OutlineEditAction): void {
    if (this.editAction === action) return;
    this.editAction = action;
    this.controller.reset();
    this.smoothTogglePending = null;
    this.onEditActionChanged?.();
  }

  getEditAction(): OutlineEditAction {
    return this.editAction;
  }

  // ---- OutlineEditHost ------------------------------------------------

  getScope(): paper.PaperScope {
    return this.ctx.scene.getPaperScope();
  }

  getScene(): Scene {
    return this.ctx.scene;
  }

  getSettings(): ToolSettings {
    return this.ctx.settings;
  }

  /** The edit spec for the current selection, or null when the selected
   *  object is missing / construction-derived / cropped.
   *
   *  Delegated to the Scene so the tool edits exactly what the overlay drew
   *  handles on (#1033). It used to answer this itself from a Leather /
   *  Cut-out / Stitch mode, which is the thing that ticket removes: the mode
   *  was a second, separately-maintained answer to a question the selection
   *  already settles. */
  private currentSpec(): OutlineEditHandlesSpec | null {
    return this.ctx.scene.outlineEditSpec();
  }

  /** The live model object carrying the outline being edited — a piece, a
   *  cutout, or (since seams gained curves, #982) a stitch line. */
  private lockCarrier(): Piece | Cutout | StitchLine | null {
    const spec = this.currentSpec();
    if (!spec) return null;
    const piece = this.ctx.history.state.pieces.find(
      (p) => p.id === spec.pieceId,
    );
    if (!piece) return null;
    if (spec.kind === "piece") return piece;
    if (spec.kind === "cutout")
      return piece.cutouts.find((c) => c.id === spec.cutoutId) ?? null;
    return pieceStitchLines(piece).find((l) => l.id === spec.stitchId) ?? null;
  }

  /** Shift `pathData` between the part's own space and where it is drawn.
   *
   *  In the Spread view a moved linked part is drawn at its cut-layout offset,
   *  and so is everything inside it (#1140). This tool works in DISPLAYED
   *  space throughout — the cursor, the live item it drags, the handles drawn
   *  from it — and converts only where it meets the model: reading an outline
   *  in (`"display"`) and writing one back (`"model"`). A no-op everywhere
   *  else, so the common case costs nothing. */
  private shiftPath(
    pathData: string,
    pieceId: string,
    to: "display" | "model",
  ): string {
    const piece = this.ctx.history.state.pieces.find((p) => p.id === pieceId);
    if (!piece) return pathData;
    const { dx, dy } = pieceDisplayOffset(
      piece,
      this.ctx.scene.getPartLayoutView(),
    );
    if (dx === 0 && dy === 0) return pathData;
    const sign = to === "display" ? 1 : -1;
    return translatePathData(pathData, sign * dx, sign * dy, this.getScope());
  }

  getTarget(): OutlineTarget | null {
    const spec = this.currentSpec();
    if (!spec) return null;
    const toDisplay = (d: string) => this.shiftPath(d, spec.pieceId, "display");
    const toModel = (d: string) => this.shiftPath(d, spec.pieceId, "model");
    const lockModeAt = (ref: OutlineAnchorRef) => {
      const carrier = this.lockCarrier();
      const entry = (carrier?.smoothAnchors ?? []).find(
        (a: { pathIndex: number; segmentIndex: number; mode?: string }) =>
          a.pathIndex === ref.pathIndex && a.segmentIndex === ref.segmentIndex,
      );
      return entry ? (entry.mode ?? "smooth") : null;
    };
    if (spec.kind === "piece") {
      return {
        key: `piece:${spec.pieceId}`,
        mustStayClosed: true,
        minAnchors: 3,
        allowCurves: true,
        noun: "part outline",
        lockModeAt,
        pathData: () =>
          toDisplay(
            this.ctx.history.state.pieces.find((p) => p.id === spec.pieceId)
              ?.shapePathData ?? "",
          ),
        liveItem: () =>
          this.ctx.scene.getOutlineEditItem(spec) as paper.PathItem | null,
        refreshHandles: () => this.ctx.scene.refreshOutlineEditHandles(),
        commit: (label, displayed) => {
          const pathData = toModel(displayed);
          // A part outline is always a closed shape — refuse any edit
          // that would open it (belt-and-braces; the geometry helpers
          // already preserve closure).
          if (!allSubpathsClosed(this.getScope(), pathData)) return;
          this.ctx.commit(label, (proj) =>
            updatePiece(proj, spec.pieceId, (pc) =>
              // A direct point edit takes ownership of the geometry: drop any
              // rounding metadata so the derive pass stops rebuilding the
              // outline from its now-stale sharp source and silently undoing
              // this edit (#974). The rounded geometry the user was editing
              // becomes the new source, as construction does on its first
              // destructive edit.
              // A direct point edit also takes ownership from the freehand
              // trace (#1016): the trace no longer describes what's on screen,
              // and re-deriving from it would undo this edit on the next
              // commit — the same reason the rounding metadata goes.
              markFreehandEdited(
                clearPieceRounding({ ...pc, shapePathData: pathData }),
              ),
            ),
          );
        },
      };
    }
    if (spec.kind === "cutout") {
      return {
        key: `cutout:${spec.pieceId}:${spec.cutoutId}`,
        mustStayClosed: true,
        minAnchors: 3,
        allowCurves: true,
        noun: "cutout",
        lockModeAt,
        pathData: () =>
          toDisplay(
            this.ctx.history.state.pieces
              .find((p) => p.id === spec.pieceId)
              ?.cutouts.find((c) => c.id === spec.cutoutId)?.pathData ?? "",
          ),
        liveItem: () =>
          this.ctx.scene.getOutlineEditItem(spec) as paper.PathItem | null,
        refreshHandles: () => this.ctx.scene.refreshOutlineEditHandles(),
        commit: (label, displayed) => {
          const pathData = toModel(displayed);
          if (!allSubpathsClosed(this.getScope(), pathData)) return;
          this.ctx.commit(label, (proj) =>
            updatePiece(proj, spec.pieceId, (pc) => ({
              ...pc,
              cutouts: pc.cutouts.map((c) =>
                // Same rounding handoff as the part outline above (#974).
                c.id === spec.cutoutId
                  ? markFreehandEdited(clearPieceRounding({ ...c, pathData }))
                  : c,
              ),
            })),
          );
        },
      };
    }
    const stitchLine = () => {
      const piece = this.ctx.history.state.pieces.find(
        (p) => p.id === spec.pieceId,
      );
      return piece
        ? pieceStitchLines(piece).find((l) => l.id === spec.stitchId)
        : undefined;
    };
    return {
      key: `stitch:${spec.pieceId}:${spec.stitchId}`,
      mustStayClosed: !!stitchLine()?.closed,
      minAnchors: stitchLine()?.closed ? 3 : 2,
      noun: "seam",
      // Seams carry real bezier geometry now (#982), so the same curve editing
      // a part outline gets — drag a run to bend it, pull tangents, toggle a
      // corner smooth — works here too.
      allowCurves: true,
      // A seam's locks live in its own `smoothAnchors`, which `lockCarrier`
      // already resolves. This was simply missing, so a seam was the one
      // target still reading its locks out of the handle geometry (#1099).
      lockModeAt,
      pathData: () => {
        const line = stitchLine();
        if (!line || line.points.length < 2) return "";
        return toDisplay(seamPathData(line));
      },
      liveItem: () =>
        this.ctx.scene.getOutlineEditItem(spec) as paper.PathItem | null,
      refreshHandles: () => this.ctx.scene.refreshOutlineEditHandles(),
      // A seam's visible guide AND its chisel marks are re-derived from the
      // MODEL, so a drag on the live phantom shows nothing until release
      // without this (#982). Push the in-flight geometry through as a preview
      // piece and the canvas re-lays the marks under the cursor.
      previewLive: () => {
        const item = this.ctx.scene.getOutlineEditItem(
          spec,
        ) as paper.PathItem | null;
        if (!item) return;
        const seam = seamFromPathData(toModel(item.pathData), this.getScope());
        if (!seam) return;
        const piece = this.ctx.history.state.pieces.find(
          (p) => p.id === spec.pieceId,
        );
        if (!piece) return;
        this.ctx.scene.setEditDragPreview({
          ...piece,
          stitchLines: pieceStitchLines(piece).map((l) =>
            l.id === spec.stitchId
              ? {
                  ...l,
                  points: seam.points,
                  handles: seam.handles,
                  closed: (seam.closed && seam.points.length >= 3) || undefined,
                }
              : l,
          ),
        });
      },
      clearPreview: () => this.ctx.scene.clearEditDragPreview(),
      commit: (label, pathData, meta) => {
        // Map the edited path back to the seam's anchors AND handles; the hole
        // row re-derives from the new geometry on this commit.
        const seam = seamFromPathData(toModel(pathData), this.getScope());
        if (!seam) return;
        this.ctx.commit(label, (proj) =>
          updatePiece(proj, spec.pieceId, (pc) => ({
            ...pc,
            stitchLines: (pc.stitchLines ?? []).map((l) =>
              l.id === spec.stitchId
                ? {
                    // `markFreehandEdited` is the BASE, not an overlay: a
                    // spread can't delete a key, so `{...l, ...cleaned}` would
                    // leave `rawPoints` from `l` intact and the seam would
                    // re-derive straight back over this edit (#1016).
                    ...markFreehandEdited(l),
                    points: seam.points,
                    handles: seam.handles,
                    // Anchor locks + bezier memory ride along in the SAME
                    // commit as the geometry they index into (#982) — but ONLY
                    // when a remap was supplied. The drag commits don't send
                    // one (they don't renumber anchors), and overwriting with
                    // `undefined` there would wipe the seam's locks on every
                    // handle drag; the piece target preserves them the same way,
                    // by spreading rather than assigning.
                    ...(meta
                      ? {
                          smoothAnchors: meta.smoothAnchors?.length
                            ? meta.smoothAnchors
                            : undefined,
                          bezierMemory: meta.bezierMemory?.length
                            ? meta.bezierMemory
                            : undefined,
                        }
                      : {}),
                    // `authoredPoints` is the pre-crop geometry a later MOVE
                    // re-crops from (#780). Editing the seam by hand makes what
                    // the user just drew the authored shape, so a stale copy
                    // would resurrect the old curve on the next move.
                    authoredPoints: undefined,
                    authoredClosed: undefined,
                  }
                : l,
            ),
          })),
        );
      },
    };
  }

  refreshEditCursor(p: { x: number; y: number }): void {
    this.applyHoverCursor(p);
  }

  // ---- Tool lifecycle -------------------------------------------------

  override onActivate(): void {
    this.editAction = "drag";
    this.onEditActionChanged?.();
    this.ctx.scene.setOutlineEditActive(true);
  }

  override onDeactivate(): void {
    this.controller.reset();
    this.smoothTogglePending = null;
    this.ctx.scene.setOutlineEditActive(false);
    this.ctx.scene.setBezierToggleAffordance(null);
    setCanvasCursor(this.getScope(), "default");
  }

  override onDown(_s: PointerSample, p: { x: number; y: number }): void {
    const scope = this.getScope();
    const pt = new scope.Point(p.x, p.y);
    const target = this.getTarget();
    if (target) {
      const handle = this.controller.findHandleHit(p);
      if (this.editAction === "add-point") {
        // Commit on press — no drag gesture. A press on an existing
        // handle is a no-op rather than a duplicate insert.
        if (!handle) {
          const curveHit = this.controller.findCurveHit(pt);
          if (curveHit) {
            const result = insertAnchorOnPathData(
              scope,
              target.pathData(),
              curveHit,
            );
            if (result) {
              this.commitOutlineChange(
                target,
                `Add point to ${target.noun}`,
                result.pathData,
                (locks, memory) =>
                  remapAnchorMetaAfterInsert(locks, memory, {
                    pathIndex: curveHit.pathIndex,
                    insertedAt: result.insertedAt,
                    dividedCurveHadHandles: result.dividedCurveHadHandles,
                  }),
              );
              this.refreshEditCursor(p);
            }
            return;
          }
        } else {
          return;
        }
      } else if (this.editAction === "delete") {
        if (handle?.kind === "anchor") {
          const beforePath = target.pathData();
          const next = deleteOutlineAnchor(scope, beforePath, handle, {
            minAnchors: target.minAnchors,
          });
          if (next) {
            const counts = this.subpathAnchorCount(
              beforePath,
              handle.pathIndex,
            );
            this.commitOutlineChange(
              target,
              `Delete point from ${target.noun}`,
              next,
              (locks, memory) =>
                remapAnchorMetaAfterDelete(locks, memory, handle, {
                  anchorCount: counts.count,
                  closed: counts.closed,
                }),
            );
            this.refreshEditCursor(p);
          } else {
            showCanvasToast(
              target.mustStayClosed
                ? "A closed outline needs at least 3 points — delete refused"
                : "A seam needs at least 2 points — delete refused",
            );
          }
          return;
        }
        if (handle?.kind === "bezier-tip") {
          // Deleting a tangent tip straightens that side of the curve.
          const cp = new scope.CompoundPath({
            pathData: target.pathData(),
            insert: false,
          });
          const seg =
            resolveSubpaths(cp)[handle.pathIndex]?.segments[
              handle.segmentIndex
            ];
          if (seg) {
            if (handle.side === "in") seg.handleIn = new scope.Point(0, 0);
            else seg.handleOut = new scope.Point(0, 0);
            const pathData = cp.pathData;
            cp.remove();
            // A one-sided handle can't satisfy any lock — clear the
            // anchor's entry (pathDeletion.deleteHandle contract).
            this.commitOutlineChange(
              target,
              `Delete ${target.noun} handle`,
              pathData,
              (locks, memory) => ({
                smoothAnchors: locks?.filter(
                  (a) =>
                    a.pathIndex !== handle.pathIndex ||
                    a.segmentIndex !== handle.segmentIndex,
                ),
                bezierMemory: memory?.filter(
                  (m) =>
                    m.pathIndex !== handle.pathIndex ||
                    m.segmentIndex !== handle.segmentIndex,
                ),
              }),
            );
          } else {
            cp.remove();
          }
          return;
        }
        // Fall through to re-targeting below.
      } else if (this.editAction === "smooth-toggle") {
        if (!target.allowCurves) {
          showCanvasToast("Stitch points are always sharp corners");
          return;
        }
        // Tangent tips + curve strokes stay draggable in smooth-toggle
        // (only anchor-node movement is disabled) — parity with
        // construction. A tip press starts a handle drag; a stroke
        // press starts a curve bend; an anchor / near-anchor press
        // becomes a pending tap whose generous-radius lookup on
        // release toggles the nearest anchor (fingertip-friendly).
        if (handle?.kind === "bezier-tip") {
          if (this.controller.maybeStartDrag(pt, handle)) return;
        } else if (!handle && this.controller.maybeStartDrag(pt, null)) {
          return;
        }
        this.smoothTogglePending = { x: p.x, y: p.y };
        return;
      } else {
        // Default drag action: bezier tip → anchor → curve stroke.
        if (this.controller.maybeStartDrag(pt, handle)) return;
      }
    }
    this.retargetFromHit(pt);
  }

  override onMove(s: PointerSample, p: { x: number; y: number }): void {
    if (this.smoothTogglePending) {
      const dx = p.x - this.smoothTogglePending.x;
      const dy = p.y - this.smoothTogglePending.y;
      if (Math.hypot(dx, dy) > 3) this.smoothTogglePending = null;
      return;
    }
    if (this.controller.handleMove(s, p)) return;
  }

  override onUp(_s: PointerSample, p: { x: number; y: number }): void {
    if (this.smoothTogglePending) {
      const tap = this.smoothTogglePending;
      this.smoothTogglePending = null;
      this.applySmoothToggleTap(tap);
      return;
    }
    if (this.controller.handleUp(p)) return;
  }

  override onHover(_s: PointerSample, p: { x: number; y: number }): void {
    this.applyHoverCursor(p);
  }

  /** Dragging a point, a tangent handle or a run is deliberate placement of an
   *  existing outline: a second contact must not abandon it (#40), and a
   *  browser-cancelled pointer commits it where it was (#1112). */
  override gestureSurvivesSecondContact(): boolean {
    return this.controller.hasActiveDrag();
  }

  override onCancel(): void {
    this.controller.reset();
    this.smoothTogglePending = null;
    // Re-render drops any half-applied live mutation back to the
    // committed model state.
    this.ctx.rerender();
  }

  // ---- internals ------------------------------------------------------

  /** A click that didn't hit a handle re-targets the edited object — to
   *  WHATEVER was clicked (#1033), or an explanation of why that object's
   *  points can't be edited.
   *
   *  The hit resolves most-specific-first: a seam sits on top of the cut-out
   *  or part it belongs to, and a cut-out on top of its part, so the innermost
   *  thing under the cursor is the one the user is pointing at. Before #1033
   *  this branched on a Leather / Cut-out / Stitch mode, which meant clicking a
   *  seam while in Leather mode silently retargeted the PART under it. */
  private retargetFromHit(pt: paper.Point): void {
    const project = this.ctx.history.state;
    // The shared leather picker, for two reasons beyond not writing a third
    // copy of this. It resolves a seam from its PATH rather than from the
    // painted chisel marks, so a seam is pickable along the run between its
    // holes — and, crucially here, whether or not the current display mode is
    // drawing it. A raw `hitTest` can't see a seam while the canvas is in the
    // structural view, which would leave the tool unable to ever retarget onto
    // one: the seam isn't drawn until it's selected, and it can't be selected
    // until it's drawn. The picker also treats a cut-out's VOID as the cut-out
    // instead of as nothing.
    const pick = pickLeatherObjectAt(
      this.ctx.scene,
      project,
      this.getScope(),
      { x: pt.x, y: pt.y },
      { includeStitch: true },
    );
    if (!pick) return;
    const piece = project.pieces.find((pc) => pc.id === pick.pieceId);
    if (!piece) return;
    const base = {
      pieceId: pick.pieceId,
      decorationId: null,
      constructionId: null,
    } as const;
    if (pick.kind === "stitch") {
      const line = pieceStitchLines(piece).find((l) => l.id === pick.stitchId);
      if (line?.constructionOutline) {
        showCanvasToast(LINKED_STITCH_HINT);
        return;
      }
      if (line?.authoredPoints) {
        showCanvasToast(CROPPED_STITCH_HINT);
        return;
      }
      this.ctx.setSelection({ ...base, stitchId: pick.stitchId });
      return;
    }
    if (pick.kind === "cutout") {
      const cutout = piece.cutouts.find((c) => c.id === pick.cutoutId);
      if (cutout?.constructionOutline) {
        showCanvasToast(LINKED_CUTOUT_HINT);
        return;
      }
      this.ctx.setSelection({ ...base, cutoutId: pick.cutoutId });
      return;
    }
    if (piece.constructionOutline) {
      showCanvasToast(LINKED_PIECE_HINT);
      return;
    }
    this.ctx.setSelection(base);
    // Selection change re-renders; the overlay derives from it.
  }

  /** Commit an outline geometry change together with the anchor
   *  metadata remap it implies. Every target — piece, cutout and, since seams
   *  gained curves (#982), stitch — carries locks + bezier memory that travel
   *  with their anchors. */
  private commitOutlineChange(
    target: OutlineTarget,
    label: string,
    pathData: string,
    remap: (
      locks: NonNullable<Piece["smoothAnchors"]> | undefined,
      memory: NonNullable<Piece["bezierMemory"]> | undefined,
    ) => OutlineMeta,
  ): void {
    const spec = this.currentSpec();
    if (!spec) {
      target.commit(label, pathData);
      return;
    }
    if (spec.kind === "stitch") {
      // A seam is the one target that may be OPEN, so it skips the closure
      // check. Its geometry commit lives on the target (it writes points +
      // handles, not pathData) and carries the remapped metadata with it — ONE
      // commit, so one undo takes the whole edit back.
      const line = this.ctx.history.state.pieces
        .find((p) => p.id === spec.pieceId)
        ?.stitchLines?.find((l) => l.id === spec.stitchId);
      target.commit(
        label,
        pathData,
        remap(line?.smoothAnchors, line?.bezierMemory),
      );
      return;
    }
    // `pathData` is in displayed space, like everything else here.
    const modelPath = this.shiftPath(pathData, spec.pieceId, "model");
    if (!allSubpathsClosed(this.getScope(), modelPath)) return;
    this.ctx.commit(label, (proj) =>
      updatePiece(proj, spec.pieceId, (pc) => {
        if (spec.kind === "piece") {
          const remapped = remap(pc.smoothAnchors, pc.bezierMemory);
          return {
            ...pc,
            shapePathData: modelPath,
            smoothAnchors: remapped.smoothAnchors?.length
              ? remapped.smoothAnchors
              : undefined,
            bezierMemory: remapped.bezierMemory?.length
              ? remapped.bezierMemory
              : undefined,
          };
        }
        return {
          ...pc,
          cutouts: pc.cutouts.map((c) => {
            if (c.id !== spec.cutoutId) return c;
            const remapped = remap(c.smoothAnchors, c.bezierMemory);
            return {
              ...c,
              pathData: modelPath,
              smoothAnchors: remapped.smoothAnchors?.length
                ? remapped.smoothAnchors
                : undefined,
              bezierMemory: remapped.bezierMemory?.length
                ? remapped.bezierMemory
                : undefined,
            };
          }),
        };
      }),
    );
  }

  /** Anchor count + closed-ness of one subpath of `pathData`. */
  private subpathAnchorCount(
    pathData: string,
    pathIndex: number,
  ): { count: number; closed: boolean } {
    const scope = this.getScope();
    try {
      const cp = new scope.CompoundPath({ pathData, insert: false });
      const sub = resolveSubpaths(cp)[pathIndex];
      const out = {
        count: sub?.segments.length ?? 0,
        closed: !!sub?.closed,
      };
      cp.remove();
      return out;
    } catch {
      return { count: 0, closed: false };
    }
  }

  /** Parse an edited polyline pathData back to seam points. Returns
   *  null when the path failed to parse. Closed-ness stays on the
   *  model (drags never change topology). */
  private pathDataToPoints(pathData: string): Vec2[] | null {
    const scope = this.getScope();
    let cp: paper.CompoundPath | null = null;
    try {
      cp = new scope.CompoundPath({ pathData, insert: false });
    } catch {
      return null;
    }
    const sub = resolveSubpaths(cp)[0];
    if (!sub) {
      cp.remove();
      return null;
    }
    const points = sub.segments.map((seg) => ({
      x: seg.point.x,
      y: seg.point.y,
    }));
    cp.remove();
    return points;
  }

  private applySmoothToggleTap(tap: { x: number; y: number }): void {
    const target = this.getTarget();
    if (!target || !target.allowCurves) return;
    const spec = this.currentSpec();
    if (!spec) return;
    const scope = this.getScope();
    // Generous fingertip radius — same forgiving pickup as the
    // construction smooth-toggle (nearest anchor within ~3 mm).
    const item = target.liveItem();
    if (!item) return;
    const handle = this.controller.findHandleHit(tap);
    let ref: { pathIndex: number; segmentIndex: number } | null =
      handle?.kind === "anchor" ? handle : null;
    if (!ref) {
      const zoom = scope.view?.zoom ?? 1;
      const radius = Math.max(3, 12 / zoom);
      let best: {
        pathIndex: number;
        segmentIndex: number;
        distance: number;
      } | null = null;
      resolveSubpaths(item).forEach((sub, pathIndex) => {
        sub.segments?.forEach((seg, segmentIndex) => {
          const d = Math.hypot(seg.point.x - tap.x, seg.point.y - tap.y);
          if (d <= radius && (!best || d < best.distance)) {
            best = { pathIndex, segmentIndex, distance: d };
          }
        });
      });
      ref = best;
    }
    if (!ref) return;
    const carrier = this.lockCarrier();
    if (!carrier) return;
    // The SAME four-state bezier cycle construction uses (#969 parity):
    // corner → independent → angle lock → smooth lock → corner, with
    // handle snapshots restored from bezierMemory.
    const step = cycleAnchorBezier(
      {
        pathData: target.pathData(),
        smoothAnchors: carrier.smoothAnchors,
        bezierMemory: carrier.bezierMemory,
      },
      ref.pathIndex,
      ref.segmentIndex,
      scope,
    );
    if (!step) return;
    // The cycle ran on the displayed outline; store it in the part's own space.
    const modelPath = this.shiftPath(step.pathData, spec.pieceId, "model");
    // A seam stores anchors + handles, not pathData, so its cycle result has to
    // be read back through `seamFromPathData` (#982).
    const seam =
      spec.kind === "stitch" ? seamFromPathData(modelPath, scope) : null;
    if (spec.kind === "stitch" && !seam) return;
    this.ctx.commit(step.label, (proj) =>
      updatePiece(proj, spec.pieceId, (pc) => {
        if (spec.kind === "piece") {
          return {
            ...pc,
            shapePathData: modelPath,
            smoothAnchors: step.smoothAnchors,
            bezierMemory: step.bezierMemory,
          };
        }
        if (spec.kind === "stitch") {
          return {
            ...pc,
            stitchLines: (pc.stitchLines ?? []).map((l) =>
              l.id === spec.stitchId
                ? {
                    ...markFreehandEdited(l),
                    points: seam!.points,
                    handles: seam!.handles,
                    smoothAnchors: step.smoothAnchors,
                    bezierMemory: step.bezierMemory,
                  }
                : l,
            ),
          };
        }
        return {
          ...pc,
          cutouts: pc.cutouts.map((c) =>
            c.id === spec.cutoutId
              ? {
                  ...c,
                  pathData: modelPath,
                  smoothAnchors: step.smoothAnchors,
                  bezierMemory: step.bezierMemory,
                }
              : c,
          ),
        };
      }),
    );
  }

  /** Hover cursor — mirrors construction's edit-mode glyphs per
   *  action (`ConstructionTool.refreshEditCursor`):
   *   - drag: handle → move, curve → grab
   *   - add-point: curve → copy (handle / empty → default)
   *   - delete: a deletable handle → not-allowed
   *   - smooth-toggle: anchor → pointer, tip → move, curve → grab */
  private applyHoverCursor(p: { x: number; y: number }): void {
    const scope = this.getScope();
    if (this.controller.hasActiveDrag()) return;
    const target = this.getTarget();
    // The bezier-toggle glyph belongs to exactly one hover state (#147), so
    // clear it up front and let that one state put it back — a per-branch
    // clear is how a cursor affordance ends up stuck on screen.
    this.ctx.scene.setBezierToggleAffordance(null);
    if (!target) {
      setCanvasCursor(scope, "default");
      return;
    }
    const handle = this.controller.findHandleHit(p);
    const pt = new scope.Point(p.x, p.y);
    const hasCurve = () => this.controller.findCurveHit(pt) !== null;

    if (this.editAction === "delete") {
      // Only handles are deletable on a leather outline (deleting a
      // segment would open the shape), so the "remove" glyph shows
      // over a handle, default elsewhere.
      setCanvasCursor(scope, handle ? "not-allowed" : "default");
      return;
    }
    if (this.editAction === "add-point") {
      setCanvasCursor(scope, !handle && hasCurve() ? "copy" : "default");
      return;
    }
    if (this.editAction === "smooth-toggle") {
      if (handle?.kind === "anchor") {
        // #147: the pointer says "this is clickable", the glyph beside it says
        // what the click DOES — turn this point between a corner and a curve.
        // The glyph is the sub-toolbar's own smooth/sharp icon, so the button
        // and the cursor say the same thing.
        setCanvasCursor(scope, "pointer");
        this.ctx.scene.setBezierToggleAffordance(p);
        return;
      }
      if (handle?.kind === "bezier-tip") setCanvasCursor(scope, "move");
      else if (hasCurve()) setCanvasCursor(scope, "grab");
      else setCanvasCursor(scope, "default");
      return;
    }
    // Drag (default): handle → move, curve → grab.
    if (handle) setCanvasCursor(scope, "move");
    else if (hasCurve()) setCanvasCursor(scope, "grab");
    else setCanvasCursor(scope, "default");
  }
}
